import React, { Component, createRef } from 'react';
import { withTranslation } from 'react-i18next';
import { LayoutSidebar } from '../../Layout';
import { DataExplorerTable } from './DataExplorerTable';
import { DataExplorerLineChart } from './DataExplorerLineChart';
import { APIClient } from '../../utils/APIClient';
import { API_URL, DATA_EXPLORER_API_URL } from '../../settings/local_settings';
import { DataExplorerHeader } from './DataExplorerHeader';
import { Spinner } from '../Spinner';
import { FieldSensitivityService } from '../../utils/FieldSensitivityService';
import { ChartControls } from './ChartControls';
import DataExplorerJsonModal from './DataExplorerJsonModal';
import { ConfigureEventsModal } from './ConfigureEventsModal';
import { DataExplorerUpgradePage } from './DataExplorerUpgradePage';
import { DataExplorerChartsSection } from './DataExplorerChartsSection';
import { AccessService } from '../../utils/AccessService';
import { LocalStorage } from '../../utils/LocalStorage';
import moment from 'moment';
import { exportReportToPDF, buildPdfTableData } from '../../utils/pdfExport';
import { DataExplorerToolbar } from './DataExplorerToolbar';
import { encodeFiltersToUrl, readClientIdFromUrl } from '../../utils/DataExplorerUrlParams';
import { toast } from 'react-toastify';
import { getPeriodName } from '../../periods';
import { SaveAsViewModal } from './SavedViews/SaveAsViewModal';
import { SavedViewsCommandPalette } from './SavedViews/SavedViewsCommandPalette';
import { readViewIdFromUrl, readReplicatedFromUrl, buildLoadViewUrl, viewToFilters } from '../../utils/savedViews';
import { YesNoModal } from '../elements/YesNoModal';
import { PortalReportsApi } from '../../api/portalReports';
import { DataExplorerEmptyState, EMPTY_STATE_KINDS } from './DataExplorerEmptyState';
import { typedErrorCode, typedErrorParams, typedQueryErrorMessage } from './typedQueryError';
import { filterErrorText } from '../../utils/filterNotes';

// Errores que se arreglan cambiando la consulta, no reintentándola.
const NON_RETRYABLE_QUERY_ERRORS = new Set(['DIMENSION_FILTER_NOT_SUPPORTED', 'INVALID_FIELD']);

class DataExplorerPageComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            hasAccess: null,
            loading: false,
            results: null,
            isDownloading: false,
            fieldsData: null,
            chartMetrics: [null, null], // [metric1, metric2] - hasta 2 métricas para el gráfico
            chartTimePeriod: null, // Período de tiempo seleccionado para el gráfico
            availableTimePeriods: [], // Períodos disponibles que vienen del backend
            defaultTimePeriod: null, // Período por defecto que viene del backend
            sourceType: null, // Necesario para ChartControls
            lastFilters: null, // Guardar los últimos filtros para poder hacer pedidos adicionales
            currentViewState: null, // Estado vivo de la Vista (filtros + columnas) emitido por el Header → fuente de verdad del dirty
            chartDataCache: {}, // Cache de datos del gráfico por período: { [period]: chartData }
            isDataNodeJsonModalOpen: false,
            dataNodeRequestJson: null,
            showTooltip: true, // Toggle para mostrar/ocultar tooltip al hover sobre nodos del gráfico
            showEventsConfigModal: false,
            unconfiguredAccounts: [],
            pendingExploreFilters: null,
            showSaveAsViewModal: false, // Saved Views — modal de guardar exploración actual
            currentView: null,          // Saved Views — vista cargada (si view_id estaba en URL)
            showCommandPalette: false,  // Saved Views — palette para cargar vistas (Cmd/Ctrl+K)
            originalViewConfig: null,   // Saved Views — snapshot serializado del config al cargar la vista
            savingViewChanges: false,   // Saved Views — flag mientras se hace PATCH de cambios
            confirmExitView: false,     // Saved Views — modal de confirmación al salir con dirty
            pendingViewToLoad: null,    // Saved Views — vista a cargar tras confirmar descartar cambios (cambio de vista con dirty)
            pendingInlineViewLoad: false, // Saved Views — true entre cargar una vista en caliente y que su query setee lastFilters (evita capturar el snapshot dirty con los filtros de la vista anterior)
            replicateSuggestedName: null, // Card #2420 — nombre sugerido al guardar un borrador replicado de otro cliente
            chartPrefs: LocalStorage.getDataExplorerChartPreferences() || {}, // Saved Views — espejo de las prefs en LS
            chartPrefsVersion: 0,       // Forzar re-mount de ChartsSection al cargar otra vista
            emptyStateKind: null,       // Reemplaza alert() bloqueante con empty state inline (Card #2383)
            emptyStateMessage: null,    // Texto del backend, sólo para los códigos de `typedQueryError`
            emptyStateRetryable: true,  // «Reintentar» no se ofrece cuando el error lo corrige el usuario (QA-VIS-filtrosfe-37)
            agencyClients: [],          // Lista de clientes de la agencia (para detectar onboarding state)
            formDataLoaded: false,      // Flag que indica que ya cargamos /sync-configuration/form-data
        };
        // Variable de instancia para tracking de requests (no state para evitar race conditions)
        this.currentSearchRequestId = 0;
        this.handleExplore = this.handleExplore.bind(this);
        this.handleDownload = this.handleDownload.bind(this);
        this.handleDownloadPDF = this.handleDownloadPDF.bind(this);
        this.handleFieldsDataLoaded = this.handleFieldsDataLoaded.bind(this);
        this.handleChartMetricsChange = this.handleChartMetricsChange.bind(this);
        this.handleChartTimePeriodChange = this.handleChartTimePeriodChange.bind(this);
        this.handleExportDataNodeJson = this.handleExportDataNodeJson.bind(this);
        this.handleShowTooltipChange = this.handleShowTooltipChange.bind(this);
        this.handleShareUrl = this.handleShareUrl.bind(this);
        this.openSaveAsViewModal = this.openSaveAsViewModal.bind(this);
        this.closeSaveAsViewModal = this.closeSaveAsViewModal.bind(this);
        this.openCommandPalette = this.openCommandPalette.bind(this);
        this.closeCommandPalette = this.closeCommandPalette.bind(this);
        this.handleKeyDownForPalette = this.handleKeyDownForPalette.bind(this);
        this.handleViewUpdated = this.handleViewUpdated.bind(this);
        this.handleViewSaved = this.handleViewSaved.bind(this);
        this.handleHeaderReady = this.handleHeaderReady.bind(this);
        this.handleViewStateChange = this.handleViewStateChange.bind(this);
        this.handleDeleteView = this.handleDeleteView.bind(this);
        this.loadViewInline = this.loadViewInline.bind(this);
        this.handleExitView = this.handleExitView.bind(this);
        this.handleSaveViewChanges = this.handleSaveViewChanges.bind(this);
        this.handleDiscardViewChanges = this.handleDiscardViewChanges.bind(this);
        this.handleChartPrefsChange = this.handleChartPrefsChange.bind(this);
        this._beforeUnloadHandler = this._beforeUnloadHandler.bind(this);
        this.disableUnsavedGuard = this.disableUnsavedGuard.bind(this);
        // Refs para exportación PDF
        this.lineChartRef = createRef();
        this.tableRef = createRef();
        this.chartsSectionRef = createRef();
    }

    componentDidMount() {
        const userId = LocalStorage.fetchUserId();
        AccessService.GetUserModules(userId, (data) => {
            const modules = data.modules || [];
            this.setState({ hasAccess: modules.includes(AccessService.MODULE_DATA_EXPLORER) });
        });

        // Cargar fieldsData desde el mismo endpoint que DataExplorerHeader.
        // Guardamos también `agencyClients` para detectar "agencia sin clientes" (empty state
        // de onboarding) cuando aún no hay query ejecutada.
        new APIClient(API_URL + 'node-to-gsheet/sync-configuration/form-data').call('GET')
            .then(data => {
                if (data.fields_data) {
                    this.setState({
                        fieldsData: data.fields_data,
                        agencyClients: data.agency_clients || [],
                        formDataLoaded: true,
                    });
                }
            })
            .catch(() => {
                // Silenciar error, simplemente no cargar fieldsData
            });

        // Card #2420 — aterrizaje de un borrador replicado de OTRO cliente. El blob ?q= ya
        // trae el cliente actual + selección vacía + métricas/dims/período (auto-ejecuta). Acá
        // solo capturamos el nombre de la vista origen (la URL la limpia el Header después) para
        // el toast de bienvenida y pre-cargar el nombre en "Guardar como vista". No hay ?view_id=
        // → es un borrador, no dispara el banner de vista cargada.
        if (this._isAgencyUser()) {
            const replicatedFrom = readReplicatedFromUrl();
            if (replicatedFrom) {
                this.setState({ replicateSuggestedName: replicatedFrom });
                setTimeout(() => {
                    toast.info(this.props.t('saved_views.replicate_landing', { name: replicatedFrom }));
                }, 600);
            }
        }

        // Saved Views — si la URL trae ?view_id=N (carga desde el palette o share),
        // o si LocalStorage tiene una vista persistida (caso F5/refresh), cargarla en el banner.
        // Leemos URL ANTES de que el Header limpie los URL params (race-safe: Page mounts antes).
        // NO seteamos originalViewConfig acá: se setea en componentDidUpdate cuando `lastFilters`
        // se popula (post-query), para que el snapshot tenga el mismo shape que el comparator.
        if (this._isAgencyUser()) {
            // Landing fresco en un cliente puntual (?client_id=): no restaurar una vista
            // persistida — sería de otro cliente y dispararía el auto-exit con un toast confuso.
            // (los links ?client_id= nunca traen ?view_id=, así que son mutuamente excluyentes.)
            const clientIdFromUrl = readClientIdFromUrl();
            if (clientIdFromUrl) {
                LocalStorage.clearLastLoadedViewId();
            }
            const viewIdFromUrl = readViewIdFromUrl();
            const viewIdFromLs = clientIdFromUrl ? null : LocalStorage.fetchLastLoadedViewId();
            const viewId = viewIdFromUrl || viewIdFromLs;
            if (viewId) {
                PortalReportsApi.getView(viewId).then((view) => {
                    if (view && view.status !== 'ERROR' && view.id) {
                        LocalStorage.saveLastLoadedViewId(view.id);
                        // Si la vista trae chart_preferences persistidas, sobrescribimos
                        // las del LS local para que los sub-componentes (MetricCards,
                        // BarChart, PieChart) las lean al re-montarse (key cambia).
                        if (view.config && view.config.chart_preferences) {
                            LocalStorage.saveDataExplorerChartPreferences(view.config.chart_preferences);
                            this.setState({
                                currentView: view,
                                chartPrefs: view.config.chart_preferences,
                                chartPrefsVersion: this.state.chartPrefsVersion + 1,
                            });
                        } else {
                            this.setState({ currentView: view });
                        }
                    } else {
                        LocalStorage.clearLastLoadedViewId();
                    }
                }).catch(() => { /* silent */ });
            }
        }

        // Cmd+K / Ctrl+K para abrir el command palette de vistas guardadas.
        document.addEventListener('keydown', this.handleKeyDownForPalette);
    }

    componentDidUpdate() {
        // NOTA: cambiar el cliente NO saca de la Vista — la Vista se mantiene y los items se
        // blanquean (handleChange), por lo que pasa a "Modificada". El usuario decide Resetear a
        // la vista o Guardar (si cambió el cliente, se guarda como vista nueva — `_viewChangedClient`).

        // Capturar el snapshot baseline de la vista cargada la primera vez que el Header emitió
        // su estado (`currentViewState`) reflejando la vista. Usamos `_buildCurrentViewConfig()`
        // para que el shape coincida exactamente con el que se compara después (sin false-positive).
        // `pendingInlineViewLoad` evita capturar mientras el estado todavía refleja la vista
        // ANTERIOR (carga en caliente A→B): se limpia recién cuando la query de B corre.
        if (this.state.currentView && !this.state.originalViewConfig && this.state.currentViewState
            && !this.state.pendingInlineViewLoad) {
            const snapshot = this._buildCurrentViewConfig();
            if (snapshot) {
                // eslint-disable-next-line react/no-did-update-set-state
                this.setState({ originalViewConfig: this._canonicalizeConfig(snapshot) });
                return;
            }
        }

        // Si hay vista cargada y dirty, activar el guard nativo del browser.
        if (this._isViewDirty()) {
            window.addEventListener('beforeunload', this._beforeUnloadHandler);
        } else {
            window.removeEventListener('beforeunload', this._beforeUnloadHandler);
        }
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyDownForPalette);
        window.removeEventListener('beforeunload', this._beforeUnloadHandler);
    }

    _beforeUnloadHandler(e) {
        e.preventDefault();
        e.returnValue = '';
        return '';
    }

    // Apaga el guard nativo de "cambios sin guardar". Lo usan los flujos que navegan a
    // propósito (abrir / replicar una vista desde el palette via window.location.href) para
    // que el browser no muestre el aviso de "estás saliendo del sitio" en una navegación
    // intencional. (P4)
    disableUnsavedGuard() {
        window.removeEventListener('beforeunload', this._beforeUnloadHandler);
    }

    _isViewDirty() {
        if (!this.state.currentView || !this.state.originalViewConfig) return false;
        const current = this._buildCurrentViewConfig();
        if (!current) return false;
        return this._canonicalizeConfig(current) !== this.state.originalViewConfig;
    }

    _canonicalizeConfig(cfg) {
        // Forma canónica para comparar dirty: campos user-controlled directamente. Incluye
        // chart_preferences (KPI cards, bar chart, pie chart) para que cambios en
        // visualizaciones disparen dirty. chart_metrics y chart_time_period del line chart
        // quedan FUERA porque los settea el backend con defaults post-query y causan false-positive.
        //
        // El ORDEN de dimensions/metrics se PRESERVA (no se ordena): el orden de columnas es
        // parte de la Vista, así reordenar columnas también cuenta como "modificada".
        if (!cfg) return null;
        const prefs = cfg.chart_preferences || {};
        const normalized = {
            source_type: cfg.source_type || null,
            selected_items: [...(cfg.selected_items || [])].map(String).sort(),
            period: cfg.period || null,
            compare: !!cfg.compare,
            compare_period: cfg.compare ? (cfg.compare_period || null) : null,
            dimensions: [...(cfg.dimensions || [])],
            metrics: (cfg.metrics || [])
                .map((m) => (typeof m === 'string' ? m : (m && m.name) || '')),
            dimensions_filters: cfg.dimensions_filters || [],
            chart_preferences: {
                cardMetrics: prefs.cardMetrics || [],
                barChartDimension: prefs.barChartDimension || null,
                barChartMetrics: prefs.barChartMetrics || [],
                pieChartDimension: prefs.pieChartDimension || null,
                pieChartMetric: prefs.pieChartMetric || null,
            },
        };
        return JSON.stringify(normalized);
    }

    handleKeyDownForPalette(e) {
        if (!this._isAgencyUser()) return;
        const isCmdOrCtrl = e.metaKey || e.ctrlKey;
        if (isCmdOrCtrl && e.key && e.key.toLowerCase() === 'k') {
            e.preventDefault();
            this.openCommandPalette();
        } else if (e.key === 'Escape' && this.state.showCommandPalette) {
            this.closeCommandPalette();
        }
    }

    openCommandPalette() {
        this.setState({ showCommandPalette: true });
    }

    closeCommandPalette() {
        this.setState({ showCommandPalette: false });
    }

    handleViewUpdated(updatedView) {
        // Sincroniza metadata (name/description/assignment) sin afectar el snapshot dirty.
        this.setState({ currentView: updatedView });
    }

    handleViewSaved(view) {
        // Callback al crear una vista nueva desde SaveAsViewModal. Setea la vista
        // como activa al instante (sin F5): banner visible, dirty=false, LS persistido
        // y URL actualizada con ?view_id=N vía replaceState (no agrega history entry).
        if (!view || !view.id) return;

        const currentConfig = this._buildCurrentViewConfig();
        this.setState({
            currentView: view,
            originalViewConfig: currentConfig ? this._canonicalizeConfig(currentConfig) : null,
            showSaveAsViewModal: false,
        });

        LocalStorage.saveLastLoadedViewId(view.id);

        try {
            const url = new URL(window.location.href);
            url.searchParams.set('view_id', String(view.id));
            window.history.replaceState({}, '', url.pathname + (url.search ? url.search : ''));
        } catch (e) {
            // No crítico: el state local + LS ya quedaron consistentes.
        }
    }

    handleHeaderReady(headerInstance) {
        // El Header se expone para poder aplicarle los filtros de una vista en caliente
        // (cambio de vista sin recargar). Ver loadViewInline / _applyViewInline.
        this.headerInstance = headerInstance;
    }

    handleViewStateChange(viewState) {
        // El Header emite su estado de Vista (filtros + columnas) ante cualquier cambio.
        // Es la fuente de verdad del dirty: al setearlo, `_isViewDirty()` se recomputa en el
        // próximo render → "Modificada" aparece al instante (incluye cambios de columnas/orden).
        this.setState({ currentViewState: viewState });
    }

    loadViewInline(view) {
        // Punto único de carga de una vista (lo usan el selector inline y el palette Cmd+K).
        // Carga en caliente sin recargar la página. Si la vista actual tiene cambios sin
        // guardar, pedimos confirmación antes de descartarlos y dejamos la vista pendiente.
        if (!view || !view.id) return;
        // Click sobre la vista ya activa: no-op (evita re-ejecutar la query y resetear scroll).
        if (this.state.currentView && String(this.state.currentView.id) === String(view.id)) {
            return;
        }
        if (this._isViewDirty()) {
            this.setState({ confirmExitView: true, pendingViewToLoad: view });
            return;
        }
        this._applyViewInline(view);
    }

    _applyViewInline(view) {
        // Replica lo que hace el montaje por URL (componentDidMount) + handleViewSaved, pero
        // en caliente: restaura chart prefs, setea la vista activa, persiste en LS, actualiza
        // la URL con ?view_id=N (replaceState, sin history entry) y aplica los filtros al Header
        // que re-ejecuta la query. originalViewConfig=null se recaptura en componentDidUpdate
        // cuando lastFilters se popula (mismo mecanismo que la carga por URL) → dirty arranca limpio.
        const prefs = view.config && view.config.chart_preferences;
        if (prefs) {
            LocalStorage.saveDataExplorerChartPreferences(prefs);
        }

        this.setState({
            currentView: view,
            originalViewConfig: null,
            // Bloquea la captura del snapshot dirty hasta que la query de ESTA vista corra y
            // setee lastFilters (sino se capturaría con los filtros de la vista anterior).
            pendingInlineViewLoad: true,
            chartPrefs: prefs || this.state.chartPrefs,
            chartPrefsVersion: this.state.chartPrefsVersion + 1,
            showCommandPalette: false,
            confirmExitView: false,
            pendingViewToLoad: null,
        });

        LocalStorage.saveLastLoadedViewId(view.id);
        window.removeEventListener('beforeunload', this._beforeUnloadHandler);

        try {
            const url = new URL(window.location.href);
            url.searchParams.set('view_id', String(view.id));
            window.history.replaceState({}, '', url.pathname + (url.search ? url.search : ''));
        } catch (e) {
            // No crítico: el state local + LS ya quedaron consistentes.
        }

        if (this.headerInstance) {
            this.headerInstance.applyExternalFilters(viewToFilters(view));
        }
    }

    handleExitView() {
        if (this._isViewDirty()) {
            this.setState({ confirmExitView: true });
            return;
        }
        this._performExitView();
    }

    _performExitView() {
        const url = new URL(window.location.href);
        if (url.searchParams.has('view_id')) {
            url.searchParams.delete('view_id');
            window.history.replaceState({}, '', url.pathname + (url.search ? url.search : ''));
        }
        LocalStorage.clearLastLoadedViewId();
        window.removeEventListener('beforeunload', this._beforeUnloadHandler);
        this.setState({ currentView: null, originalViewConfig: null, confirmExitView: false });
    }

    _viewChangedClient() {
        // True si hay una Vista cargada y el cliente VIVO del header difiere del cliente de la
        // Vista. No es un caso de error: cambiar el cliente mantiene la Vista (como "Modificada");
        // solo cambia cómo se guarda (no se puede reasignar el cliente de una Vista → vista nueva).
        const { currentView, currentViewState, lastFilters } = this.state;
        if (!currentView) return false;
        const headerClientId = (currentViewState && currentViewState.agencyClientId)
            || (lastFilters && lastFilters.agencyClientId);
        const viewClientId = currentView.agency_client_id;
        return !!headerClientId && !!viewClientId && String(headerClientId) !== String(viewClientId);
    }

    handleSaveViewChanges() {
        const { t } = this.props;
        const { currentView } = this.state;
        if (!currentView) return;
        // Si cambiaste el cliente estando en una Vista, no se puede actualizar la Vista (su
        // cliente es fijo): se guarda como Vista nueva para el cliente actual.
        if (this._viewChangedClient()) {
            toast.info(t('saved_views.save_as_new_due_to_client'));
            this.openSaveAsViewModal();
            return;
        }
        const newConfig = this._buildCurrentViewConfig();
        if (!newConfig) return;

        this.setState({ savingViewChanges: true });
        PortalReportsApi.updateView(currentView.id, { config: newConfig })
            .then((response) => {
                // NETWORK_ERROR (fallo de red, no respuesta HTTP) NO es éxito: tratarlo como error
                // para no mostrar "guardado" ni limpiar el dirty cuando en realidad no se persistió.
                if (!response || response.status === 'ERROR' || response.status === 'NETWORK_ERROR') {
                    const msg = (response && response.error_message) || t('saved_views.save_error');
                    toast.error(msg);
                    this.setState({ savingViewChanges: false });
                    return;
                }
                toast.success(t('saved_views.changes_saved'));
                this.setState({
                    savingViewChanges: false,
                    originalViewConfig: this._canonicalizeConfig(newConfig),
                    currentView: { ...currentView, config: newConfig },
                });
            })
            .catch(() => {
                toast.error(t('saved_views.save_error'));
                this.setState({ savingViewChanges: false });
            });
    }

    handleDeleteView() {
        // Borra la Vista actual. El confirm (con aviso si está asignada al portal) lo maneja el
        // selector antes de llamar acá. El backend cascadea la asignación al portal.
        const { t } = this.props;
        const { currentView } = this.state;
        if (!currentView) return;
        PortalReportsApi.deleteView(currentView.id)
            .then((response) => {
                // NETWORK_ERROR no es éxito: el backend devuelve {status:'OK'} 200 al borrar, así
                // que un fallo de red no debe simular "Vista eliminada".
                if (!response || response.status === 'ERROR' || response.status === 'NETWORK_ERROR') {
                    toast.error((response && response.error_message) || t('saved_views.delete_error'));
                    return;
                }
                toast.success(t('saved_views.view_deleted'));
                this._performExitView();
            })
            .catch(() => toast.error(t('saved_views.delete_error')));
    }

    handleChartPrefsChange() {
        // Notificación de cualquier sub-componente (MetricCards, BarChart, PieChart) cuando
        // escriben prefs a LocalStorage. Sincronizamos al state del Page para que
        // `_buildCurrentViewConfig` lea valores frescos y dirty se actualice solo.
        const fresh = LocalStorage.getDataExplorerChartPreferences() || {};
        this.setState({ chartPrefs: fresh });
    }

    handleDiscardViewChanges() {
        // Recarga la vista desde la URL con el config original.
        const { currentView } = this.state;
        if (!currentView) return;
        window.removeEventListener('beforeunload', this._beforeUnloadHandler);
        const url = buildLoadViewUrl(currentView);
        if (url) {
            window.location.href = url;
        }
    }

    handleFieldsDataLoaded(fieldsData) {
        this.setState({ fieldsData });
    }

    /**
     * El kind del empty state para un código de error del backend.
     *
     * ⚠️ Esto se llamaba `_mapErrorMessageToEmptyStateKind` y comparaba el `error_message` contra
     * estas mismas cadenas — que son CÓDIGOS. El view manda ahí el mensaje largo del nodo («Dashbo
     * perdió el acceso a cuentas publicitarias de este cliente. Cuentas afectadas: …»), así que
     * ninguna comparación matcheaba nunca y **`ACCOUNT_NO_ACCESS` era inalcanzable en producción**:
     * el Explorador caía siempre en el error genérico, sin decir qué pasaba ni ofrecer el botón a
     * Conexiones. La única que daba en el blanco era `NO_ACCESS`, y por un accidente: el 403 de
     * módulo responde `{'error_message': 'NO_ACCESS'}`, o sea un problema de suscripción pintado
     * como una cuenta desconectada.
     *
     * Desde la card `4By6KR9g` el backend manda el código tipado en `error_code`, así que se
     * resuelve por ahí. Códigos desconocidos caen en GENERIC_ERROR (no se expone el `error_message`
     * crudo por aislamiento cross-agency; la excepción vive en `typedQueryError.js`).
     */
    _emptyStateKindForCode(code) {
        if (code === 'DATA_PREPARING' ||
            code === 'DATA_NODE_DATA_IS_PREPARING_OR_NOT_CONNECTION_TO_ACCOUNTS') {
            return EMPTY_STATE_KINDS.DATA_PREPARING;
        }
        if (code === 'NO_ACCESS' || code === 'NO_CONECTION_TO_ACCOUNT' || code === 'GA4_NO_ACCESS') {
            return EMPTY_STATE_KINDS.ACCOUNT_NO_ACCESS;
        }
        return EMPTY_STATE_KINDS.GENERIC_ERROR;
    }

    /**
     * El empty state de una respuesta con error: `{kind, message}`.
     *
     * El código TIPADO manda sobre el `error_message`, que es lo que miraba el mapeo de arriba:
     * `GA4_NO_ACCESS` llega en `status`/`error_code` y nunca igualaba a la cadena `NO_ACCESS`, así
     * que una property de GA4 sin permisos caía en el error genérico en vez del estado que tiene
     * el CTA a la pantalla de conexiones.
     */
    _emptyStateFor(response) {
        const code = typedErrorCode(response);
        const message = typedQueryErrorMessage(response, this.props.i18n && this.props.i18n.language);
        if (message) {
            return { kind: EMPTY_STATE_KINDS.GENERIC_ERROR, message };
        }
        // Los errores de filtro de la tanda «Filtros que se respetan» (`copy.md` §3): el texto se
        // arma acá, en el idioma de la UI, con el campo que el backend nombre (si lo nombra). Los que
        // el usuario corrige (quitar un filtro, cambiar un campo) no llevan «Reintentar»: repetir la
        // misma consulta iba a fallar igual (QA-VIS-filtrosfe-37).
        const filterText = filterErrorText(code, typedErrorParams(response), this.props.t, this.state.fieldsData);
        if (filterText) {
            return { kind: EMPTY_STATE_KINDS.GENERIC_ERROR, message: filterText, retryable: !NON_RETRYABLE_QUERY_ERRORS.has(code) };
        }
        return { kind: this._emptyStateKindForCode(code), message: null, retryable: true };
    }

    handleChartMetricsChange(metrics) {
        const { results } = this.state;

        // Guardar preferencia en localStorage
        const prefs = LocalStorage.getDataExplorerChartPreferences() || {};
        LocalStorage.saveDataExplorerChartPreferences({ ...prefs, lineChartMetrics: metrics });

        // Verificar si las métricas seleccionadas ya tienen datos disponibles
        const hasDataForMetrics = this.checkMetricsHaveData(metrics, results);

        if (hasDataForMetrics) {
            // Si ya hay datos, solo actualizar el estado (el gráfico se refrescará automáticamente)
            this.setState({ chartMetrics: metrics });
        } else {
            // Si no hay datos, hacer un pedido al backend para obtenerlos
            this.setState({ chartMetrics: metrics });
            this.fetchChartMetricsData(metrics);
        }
    }

    checkMetricsHaveData(metrics, results) {
        if (!results || !results.chartData || !results.chartData.series) {
            return false;
        }
        
        const availableMetricIds = new Set(results.chartData.series.map(s => s.name));
        const selectedMetricIds = metrics.filter(m => m !== null);
        
        // Verificar que todas las métricas seleccionadas tengan datos
        return selectedMetricIds.every(metricId => availableMetricIds.has(metricId));
    }

    fetchChartMetricsData(metrics) {
        const { results, lastFilters } = this.state;
        if (!results || !results.tableData || !lastFilters) {
            return; // No hay datos de tabla o filtros para hacer el pedido
        }

        // Hacer un pedido al backend para obtener datos de las métricas seleccionadas
        // que no están en la tabla actual
        const { t } = this.props;
        this.setState({ loading: true });

        const data = {
            'agency_client_id': lastFilters.agencyClientId,
            'configParams': {
                'clientId': lastFilters.agencyClientId,
                'dataSourceType': lastFilters.sourceType,
            },
            'dateRange': lastFilters.periodState.selectedPeriod === 'CUSTOM' ? {
                'startDate': moment(lastFilters.periodState.range.from).format('YYYY-MM-DD'),
                'endDate': moment(lastFilters.periodState.range.to).format('YYYY-MM-DD'),
            } : { 'predefinedPeriod': lastFilters.periodState.selectedPeriod },
            'fields': [
                ...lastFilters.dimensions.map(d => ({ name: d })),
                ...lastFilters.metrics.map(m => ({ name: m })),
                // Agregar las métricas del gráfico que no están en la tabla
                ...metrics.filter(m => m !== null && !lastFilters.metrics.includes(m)).map(m => ({ name: m })),
            ],
            'compare': lastFilters.compare,
            'dimensions_filters_json': lastFilters.dimensions_filters_json || [],
        };

        // Agregar métricas del gráfico y time period
        const chartMetrics = metrics.filter(m => m !== null);
        if (chartMetrics.length > 0) {
            data.chartMetrics = chartMetrics.map(m => ({ name: m }));
        }
        
        if (this.state.chartTimePeriod) {
            const availablePeriods = this.state.availableTimePeriods || [];
            if (availablePeriods.length > 0 && availablePeriods.includes(this.state.chartTimePeriod)) {
                data.chartTimePeriod = this.state.chartTimePeriod;
            } else if (this.state.defaultTimePeriod) {
                data.chartTimePeriod = this.state.defaultTimePeriod;
            }
        } else if (this.state.defaultTimePeriod) {
            data.chartTimePeriod = this.state.defaultTimePeriod;
        }

        if (lastFilters.compare && lastFilters.comparePeriodState) {
            data.compareDateRange = lastFilters.comparePeriodState.selectedPeriod === 'CUSTOM' ? {
                'startDate': moment(lastFilters.comparePeriodState.range.from).format('YYYY-MM-DD'),
                'endDate': moment(lastFilters.comparePeriodState.range.to).format('YYYY-MM-DD'),
            } : { 'predefinedPeriod': lastFilters.comparePeriodState.selectedPeriod };
        }

        new APIClient(DATA_EXPLORER_API_URL + 'data-explorer/query/').call('POST', data)
            .then(async response => {
                // Verificar si response es null o undefined
                if (!response) {
                    this.setState({ loading: false });
                    toast.error(t('data_explorer.error_fetching_data', 'Error fetching data.'));
                    return;
                }

                if (response.status === 'ERROR') {
                    // Fetch secundario (cambio de chartMetrics): la tabla principal
                    // sigue intacta. Mostramos toast en vez de empty state full-screen.
                    toast.error(t('data_explorer.error_fetching_data', 'Error fetching data.'));
                    this.setState({ loading: false });
                } else {
                    // Actualizar solo chartData, manteniendo el resto de los resultados
                    const newState = {
                        loading: false
                    };
                    
                    if (response.chartData) {
                        // Combinar las series existentes con las nuevas
                        const currentResults = this.state.results;
                        if (currentResults && currentResults.chartData && currentResults.chartData.series) {
                            // Crear un mapa de series existentes por label (clave única)
                            const existingSeriesMap = new Map();
                            currentResults.chartData.series.forEach(serie => {
                                // Usar label como clave única (puede haber múltiples series con el mismo name pero diferentes labels)
                                const key = serie.label || `${serie.name}-default`;
                                existingSeriesMap.set(key, serie);
                            });
                            
                            // Agregar nuevas series o actualizar existentes
                            response.chartData.series.forEach(serie => {
                                const key = serie.label || `${serie.name}-default`;
                                existingSeriesMap.set(key, serie);
                            });
                            
                            // Actualizar chartData con todas las series
                            newState.results = {
                                ...currentResults,
                                chartData: {
                                    ...response.chartData,
                                    series: Array.from(existingSeriesMap.values()),
                                    // Combinar metrics también
                                    metrics: response.chartData.metrics && currentResults.chartData.metrics
                                        ? this.mergeMetrics(currentResults.chartData.metrics, response.chartData.metrics)
                                        : (response.chartData.metrics || currentResults.chartData.metrics),
                                    // Mantener labels del chartData nuevo
                                    labels: response.chartData.labels || currentResults.chartData.labels
                                }
                            };
                        } else {
                            // Si no hay datos previos, usar los nuevos directamente
                            newState.results = {
                                ...this.state.results,
                                chartData: response.chartData
                            };
                        }
                    }
                    
                    this.setState(newState);
                }
            })
            .catch(() => {
                // Fetch secundario: silencioso con toast para no romper la tabla principal.
                this.setState({ loading: false });
                toast.error(t('data_explorer.error_fetching_data', 'Error fetching data.'));
            });
    }

    mergeMetrics(existingMetrics, newMetrics) {
        // Combinar arrays de métricas, evitando duplicados por name
        const metricsMap = new Map();
        existingMetrics.forEach(m => {
            if (m && m.name) {
                metricsMap.set(m.name, m);
            }
        });
        newMetrics.forEach(m => {
            if (m && m.name) {
                metricsMap.set(m.name, m);
            }
        });
        return Array.from(metricsMap.values());
    }

    hasMetricFilters() {
        const { lastFilters, fieldsData } = this.state;
        if (!lastFilters || !lastFilters.dimensions_filters_json || !fieldsData || !fieldsData.FIELD_DATA_BY_ID || !fieldsData.FIELD_SEGMENTS) {
            return false;
        }

        // Verificar si hay algún filtro sobre una métrica
        const filters = lastFilters.dimensions_filters_json;
        for (let i = 0; i < filters.length; i++) {
            const filterGroup = filters[i];
            if (Array.isArray(filterGroup)) {
                for (let j = 0; j < filterGroup.length; j++) {
                    const filter = filterGroup[j];
                    if (filter && filter.fieldName) {
                        const field = fieldsData.FIELD_DATA_BY_ID[filter.fieldName];
                        if (field && field.segment === fieldsData.FIELD_SEGMENTS.METRICS) {
                            return true;
                        }
                    }
                }
            }
        }
        return false;
    }

    hasData() {
        const { results } = this.state;
        return results && results.tableData && results.tableData.rows && results.tableData.rows.length > 0;
    }

    handleChartTimePeriodChange(timePeriod) {
        // Validar que el período esté en la lista de disponibles
        const availablePeriods = this.state.availableTimePeriods || [];
        if (availablePeriods.length > 0 && !availablePeriods.includes(timePeriod)) {
            // Si el período no está disponible, usar el default
            const defaultPeriod = this.state.defaultTimePeriod;
            if (defaultPeriod) {
                this.setState({ chartTimePeriod: defaultPeriod });
            }
            return;
        }
        
        // Si el período es el mismo que el actual, no hacer nada
        if (this.state.chartTimePeriod === timePeriod) {
            return;
        }
        
        // Verificar si ya tenemos datos en cache para este período
        const cachedChartData = this.state.chartDataCache && this.state.chartDataCache[timePeriod];
        
        if (cachedChartData && this.state.results) {
            // Si tenemos datos en cache, actualizar solo el chartData sin hacer pedido al backend
            this.setState({
                chartTimePeriod: timePeriod,
                results: {
                    ...this.state.results,
                    chartData: cachedChartData
                }
            });
        } else {
            // Si no tenemos datos en cache, hacer pedido al backend
            this.setState({ chartTimePeriod: timePeriod });
            this.fetchChartTimePeriodData(timePeriod);
        }
    }

    fetchChartTimePeriodData(timePeriod) {
        const { results, lastFilters } = this.state;
        if (!results || !results.tableData || !lastFilters) {
            return; // No hay datos de tabla o filtros para hacer el pedido
        }

        // Hacer un pedido al backend para obtener datos con el nuevo período
        const { t } = this.props;
        this.setState({ loading: true });

        const data = {
            'agency_client_id': lastFilters.agencyClientId,
            'configParams': {
                'clientId': lastFilters.agencyClientId,
                'dataSourceType': lastFilters.sourceType,
            },
            'dateRange': lastFilters.periodState.selectedPeriod === 'CUSTOM' ? {
                'startDate': moment(lastFilters.periodState.range.from).format('YYYY-MM-DD'),
                'endDate': moment(lastFilters.periodState.range.to).format('YYYY-MM-DD'),
            } : { 'predefinedPeriod': lastFilters.periodState.selectedPeriod },
            'fields': [
                ...lastFilters.dimensions.map(d => ({ name: d })),
                ...lastFilters.metrics.map(m => ({ name: m })),
            ],
            'compare': lastFilters.compare,
            'dimensions_filters_json': lastFilters.dimensions_filters_json || [],
        };

        // Agregar métricas del gráfico y el nuevo time period
        const chartMetrics = this.state.chartMetrics.filter(m => m !== null);
        if (chartMetrics.length > 0) {
            data.chartMetrics = chartMetrics.map(m => ({ name: m }));
        }
        
        data.chartTimePeriod = timePeriod;

        if (lastFilters.compare && lastFilters.comparePeriodState) {
            data.compareDateRange = lastFilters.comparePeriodState.selectedPeriod === 'CUSTOM' ? {
                'startDate': moment(lastFilters.comparePeriodState.range.from).format('YYYY-MM-DD'),
                'endDate': moment(lastFilters.comparePeriodState.range.to).format('YYYY-MM-DD'),
            } : { 'predefinedPeriod': lastFilters.comparePeriodState.selectedPeriod };
        }

        new APIClient(DATA_EXPLORER_API_URL + 'data-explorer/query/').call('POST', data)
            .then(async response => {
                // Verificar si response es null o undefined
                if (!response) {
                    this.setState({ loading: false });
                    toast.error(t('data_explorer.error_fetching_data', 'Error fetching data.'));
                    return;
                }

                if (response.status === 'ERROR') {
                    // Fetch secundario (cambio de chartTimePeriod): la tabla queda intacta,
                    // mostramos toast en vez de empty state.
                    toast.error(t('data_explorer.error_fetching_data', 'Error fetching data.'));
                    this.setState({ loading: false });
                } else {
                    // Guardar los datos en cache para este período
                    const chartDataCache = { ...this.state.chartDataCache };
                    if (response.chartData) {
                        chartDataCache[timePeriod] = response.chartData;
                    }

                    // Actualizar chartData con los nuevos datos
                    const newState = {
                        loading: false,
                        chartDataCache
                    };

                    if (response.chartData && this.state.results) {
                        newState.results = {
                            ...this.state.results,
                            chartData: response.chartData
                        };
                    }

                    this.setState(newState);
                }
            })
            .catch(() => {
                this.setState({ loading: false });
                toast.error(t('data_explorer.error_fetching_data', 'Error fetching data.'));
            });
    }

    handleExplore(filters) {
        // Chequear si hay métricas que requieren eventos configurados
        if (filters.sourceType === 'AD_ACCOUNTS' && this.state.fieldsData) {
            const eventDependentFields = this.state.fieldsData.EVENT_DEPENDENT_FIELDS || [];
            const hasEventMetrics = filters.metrics.some(m => eventDependentFields.includes(m));

            if (hasEventMetrics) {
                new APIClient(`${API_URL}client/${filters.agencyClientId}/ad-accounts/events-check`)
                    .call('GET')
                    .then(response => {
                        if (response.data && response.data.needs_configuration) {
                            this.setState({
                                showEventsConfigModal: true,
                                unconfiguredAccounts: response.data.unconfigured_accounts,
                                pendingExploreFilters: filters,
                            });
                        } else {
                            this._executeExplore(filters);
                        }
                    })
                    .catch(() => {
                        this._executeExplore(filters);
                    });
                return;
            }
        }
        this._executeExplore(filters);
    }

    _executeExplore(filters) {
        // Incrementar ID de request sincrónicamente para evitar race conditions
        this.currentSearchRequestId += 1;
        const currentRequestId = this.currentSearchRequestId;

        this.setState({
            loading: true,
            lastFilters: filters,
            chartDataCache: {},
            chartMetrics: [null, null],
            emptyStateKind: null, // Limpiar cualquier empty state previo: la query nueva decide
            emptyStateMessage: null,
            // La query de la vista recién cargada ya corre con SUS filtros: habilitar la captura
            // del snapshot dirty (ver componentDidUpdate + _applyViewInline).
            pendingInlineViewLoad: false,
        }); // Limpiar cache y resetear métricas del gráfico al hacer nueva búsqueda

        const data = {
            'agency_client_id': filters.agencyClientId,
            'configParams': {
                'clientId': filters.agencyClientId,
                'dataSourceType': filters.sourceType,
            },
            'dateRange': filters.periodState.selectedPeriod === 'CUSTOM' ? {
                'startDate': moment(filters.periodState.range.from).format('YYYY-MM-DD'),
                'endDate': moment(filters.periodState.range.to).format('YYYY-MM-DD'),
            } : { 'predefinedPeriod': filters.periodState.selectedPeriod },
            'fields': [
                ...filters.dimensions.map(d => ({ name: d })),
                ...filters.metrics.map(m => ({ name: m })),
            ],
            'compare': filters.compare,
            'dimensions_filters_json': filters.dimensions_filters_json || [],
        };

        // No agregar métricas del gráfico en una nueva búsqueda (se resetean)
        // El time period se mantiene si está disponible
        if (this.state.chartTimePeriod) {
            const availablePeriods = this.state.availableTimePeriods || [];
            if (availablePeriods.length > 0 && availablePeriods.includes(this.state.chartTimePeriod)) {
                data.chartTimePeriod = this.state.chartTimePeriod;
            } else if (this.state.defaultTimePeriod) {
                // Si el período seleccionado no está disponible, usar el default
                data.chartTimePeriod = this.state.defaultTimePeriod;
            }
        } else if (this.state.defaultTimePeriod) {
            // Si no hay período seleccionado pero hay default, usar el default
            data.chartTimePeriod = this.state.defaultTimePeriod;
        }

        // Guardar sourceType para ChartControls
        this.setState({ sourceType: filters.sourceType });

        // Agregar el período de comparación si está habilitado
        if (filters.compare && filters.comparePeriodState) {
            data.compareDateRange = filters.comparePeriodState.selectedPeriod === 'CUSTOM' ? {
                'startDate': moment(filters.comparePeriodState.range.from).format('YYYY-MM-DD'),
                'endDate': moment(filters.comparePeriodState.range.to).format('YYYY-MM-DD'),
            } : { 'predefinedPeriod': filters.comparePeriodState.selectedPeriod };
        }

        new APIClient(DATA_EXPLORER_API_URL + 'data-explorer/query/').call('POST', data)
            .then(async response => {
                // Ignorar respuesta si ya hay una búsqueda más reciente
                if (this.currentSearchRequestId !== currentRequestId) {
                    return;
                }

                // Verificar si response es null o undefined (puede pasar si hay un error de conexión)
                if (!response) {
                    this.setState({
                        loading: false,
                        results: null,
                        emptyStateKind: EMPTY_STATE_KINDS.GENERIC_ERROR,
                        emptyStateMessage: null,
                    });
                    return;
                }

                // Verificar si es un error (puede venir como status === 'ERROR' o error_code === 409, etc.)
                if (response.status === 'ERROR' || response.error_code === 409 || (response.error_message && !response.tableData)) {
                    const emptyState = this._emptyStateFor(response);
                    this.setState({
                        loading: false,
                        results: null,
                        emptyStateKind: emptyState.kind,
                        emptyStateMessage: emptyState.message,
                        emptyStateRetryable: emptyState.retryable,
                    });
                } else {
                    // Cargar sensibilidades ANTES de actualizar el estado con los resultados
                    // Esto evita cambios de color visibles después de la carga
                    if (response.tableData && response.tableData.headers) {
                        const metricFields = response.tableData.headers
                            .filter(header => header.key && header.key.startsWith('metric_'))
                            .map(header => header.key.replace(/^metric_/, ''));
                        
                        if (metricFields.length > 0) {
                            try {
                                // Precargar sensibilidades antes de renderizar
                                await FieldSensitivityService.loadSensitivities(metricFields);
                            } catch (error) {
                                // Silenciar error, simplemente no precargar sensibilidades
                                console.error('Error precargando sensibilidades:', error);
                            }
                        }
                    }
                    
                    // Store the full response including chartData, summary, and dateRangeInfo
                    // También actualizar availableTimePeriods y defaultTimePeriod si vienen en la respuesta
                    const newState = { 
                        results: response, 
                        loading: false,
                        dataNodeRequestJson: response.dataNodeRequest
                            ? JSON.stringify(response.dataNodeRequest, null, 2)
                            : null
                    };
                    
                    if (response.chartTimePeriods) {
                        newState.availableTimePeriods = response.chartTimePeriods.available || [];
                        newState.defaultTimePeriod = response.chartTimePeriods.default || null;
                        // Si no hay time period seleccionado, usar el default
                        if (!this.state.chartTimePeriod && newState.defaultTimePeriod) {
                            newState.chartTimePeriod = newState.defaultTimePeriod;
                        }
                        // Validar que el período seleccionado siga estando disponible
                        // Si no está disponible, usar el default
                        if (this.state.chartTimePeriod && newState.availableTimePeriods.length > 0) {
                            if (!newState.availableTimePeriods.includes(this.state.chartTimePeriod)) {
                                newState.chartTimePeriod = newState.defaultTimePeriod;
                            }
                        }
                    }
                    
                    // Actualizar métricas del gráfico desde chartData.metrics si está disponible
                    // chartData.metrics ahora es un array de objetos {name, is_default}
                    if (response.chartData && response.chartData.metrics && Array.isArray(response.chartData.metrics)) {
                        // Si no hay métricas seleccionadas, intentar restaurar desde localStorage
                        const currentMetrics = this.state.chartMetrics || [null, null];
                        const hasSelectedMetrics = currentMetrics.some(m => m !== null);

                        if (!hasSelectedMetrics) {
                            const availableNames = response.chartData.metrics.map(m => m.name);
                            const prefs = LocalStorage.getDataExplorerChartPreferences();
                            const savedLineMetrics = prefs && prefs.lineChartMetrics;
                            const validSaved = savedLineMetrics
                                ? savedLineMetrics.map(m => m && availableNames.includes(m) ? m : null)
                                : null;

                            if (validSaved && validSaved.some(m => m !== null)) {
                                newState.chartMetrics = validSaved.slice(0, 2);
                            } else {
                                // Usar las métricas por defecto
                                const defaultMetrics = response.chartData.metrics
                                    .filter(m => m.is_default)
                                    .map(m => m.name)
                                    .slice(0, 2);

                                // Rellenar con null si hay menos de 2 métricas
                                while (defaultMetrics.length < 2) {
                                    defaultMetrics.push(null);
                                }
                                newState.chartMetrics = defaultMetrics.slice(0, 2);
                            }
                        }
                        // Si ya hay métricas seleccionadas, mantenerlas (no sobrescribir)
                    }
                    
                    // Guardar los datos del gráfico en cache para el período actual
                    if (response.chartData && newState.chartTimePeriod) {
                        const chartDataCache = { ...this.state.chartDataCache };
                        chartDataCache[newState.chartTimePeriod] = response.chartData;
                        newState.chartDataCache = chartDataCache;
                    }
                    
                    this.setState(newState);
                }
            })
            .catch((error) => {
                // Ignorar error si ya hay una búsqueda más reciente
                if (this.currentSearchRequestId !== currentRequestId) {
                    return;
                }
                // El error del backend decide el empty state (preparing / sin acceso / genérico),
                // y si es un error tipado del query, su mensaje va a la pantalla.
                const emptyState = this._emptyStateFor(error);
                this.setState({
                    loading: false,
                    results: null,
                    emptyStateKind: emptyState.kind,
                    emptyStateMessage: emptyState.message,
                    emptyStateRetryable: emptyState.retryable,
                });
            });
    }

    handleExportDataNodeJson() {
        if (this.state.dataNodeRequestJson) {
            this.setState({ isDataNodeJsonModalOpen: true });
        }
    }

    handleShowTooltipChange(showTooltip) {
        this.setState({ showTooltip });
    }

    handleDownload() {
        this.setState({ isDownloading: true });
        const { results } = this.state;
        if (!results || !results.tableData) {
            this.setState({ isDownloading: false });
            return;
        }

        const { headers, rows } = results.tableData;
        
        // Extract header labels
        const headerLabels = headers.map(h => h.label);
        
        // Helper function to get formatted value for CSV
        const getFormattedValueForCSV = (value, header) => {
            // Handle metric values with comparison data - use formattedCurrentValue
            if (typeof value === 'object' && value !== null && value.formattedCurrentValue !== undefined) {
                return value.formattedCurrentValue !== null ? value.formattedCurrentValue : '';
            }
            
            // Handle formatted value objects (dimensions and metrics without comparison)
            if (typeof value === 'object' && value !== null && value.formattedValue !== undefined) {
                return value.formattedValue !== null ? value.formattedValue : '';
            }
            
            // Handle simple values
            if (value === null || value === undefined) {
                return '';
            }
            
            // Convert to string
            return String(value);
        };
        
        // Convert rows to CSV format
        const csvRows = rows.map(row => {
            return headers.map(header => {
                const value = row[header.key];
                const formattedValue = getFormattedValueForCSV(value, header);
                
                // Escape commas, quotes, and newlines
                if (formattedValue.includes(',') || formattedValue.includes('"') || formattedValue.includes('\n')) {
                    return `"${formattedValue.replace(/"/g, '""')}"`;
                }
                
                return formattedValue;
            });
        });
        
        // Combine headers and rows
        const csvContent = [
            headerLabels.join(','),
            ...csvRows.map(row => row.join(','))
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.setAttribute('hidden', '');
        a.setAttribute('href', url);
        a.setAttribute('download', 'data-explorer.csv');
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        this.setState({ isDownloading: false });
    }

    handleDownloadPDF() {
        const { t } = this.props;
        const { lastFilters, fieldsData, results } = this.state;

        // Resolver nombre del cliente
        let clientName = '';
        if (lastFilters && lastFilters.agencyClients && lastFilters.agencyClientId) {
            const client = lastFilters.agencyClients.find(
                c => c.id.toString() === lastFilters.agencyClientId.toString()
            );
            if (client) clientName = client.name;
        }

        // Resolver date range
        let dateRange = '';
        if (lastFilters && lastFilters.periodState && lastFilters.periodState.selectedPeriod) {
            if (lastFilters.periodState.selectedPeriod === 'CUSTOM' && lastFilters.periodState.range) {
                const from = lastFilters.periodState.range.from
                    ? moment(lastFilters.periodState.range.from).format('DD MMM YYYY')
                    : '';
                const to = lastFilters.periodState.range.to
                    ? moment(lastFilters.periodState.range.to).format('DD MMM YYYY')
                    : '';
                dateRange = from && to ? `${from} - ${to}` : '';
            } else {
                dateRange = getPeriodName(lastFilters.periodState.selectedPeriod, t);
            }
        }

        const refs = {
            lineChartRef: this.lineChartRef,
            chartsSectionRef: this.chartsSectionRef,
        };
        const context = {
            clientName,
            dateRange,
            dimensions: lastFilters ? lastFilters.dimensions : [],
            metrics: lastFilters ? lastFilters.metrics : [],
            fieldsData,
            // El PDF escribe textos propios (título, Total, Sin desglose, deltas sin base):
            // van traducidos como el resto del Explorador, no hardcodeados en español.
            t,
            // El «N % asignado a campañas» de la nota al pie, con el formato del idioma de la pantalla.
            locale: this.props.i18n && this.props.i18n.language,
        };
        exportReportToPDF(refs, context, buildPdfTableData(results));
    }

    handleShareUrl() {
        const { t } = this.props;
        const { lastFilters } = this.state;
        if (!lastFilters) return;

        const url = encodeFiltersToUrl(lastFilters);

        navigator.clipboard.writeText(url)
            .then(() => {
                toast.success(t('data_explorer.link_copied', 'Enlace copiado al portapapeles'));
            })
            .catch(() => {
                toast.error(t('data_explorer.link_copy_error', 'No se pudo copiar el enlace'));
            });
    }

    openSaveAsViewModal() {
        if (!this.hasData()) return;
        this.setState({ showSaveAsViewModal: true });
    }

    _computeEmptyStateKind() {
        // Errores explícitos del backend (NO_ACCESS, DATA_PREPARING, etc.) tienen prioridad
        // máxima — los setea _executeExplore al recibir la respuesta.
        if (this.state.emptyStateKind) return this.state.emptyStateKind;

        // Onboarding: agencia recién creada sin clientes. Solo aplica una vez cargado
        // form-data (sino mostraríamos el empty state mientras carga).
        if (
            this.state.formDataLoaded &&
            Array.isArray(this.state.agencyClients) &&
            this.state.agencyClients.length === 0
        ) {
            return EMPTY_STATE_KINDS.NO_AGENCY_CLIENTS;
        }

        // Query terminó OK pero sin filas para esos filtros.
        if (this.state.results && !this.hasData()) {
            return EMPTY_STATE_KINDS.NO_RESULTS;
        }

        return null;
    }

    closeSaveAsViewModal() {
        this.setState({ showSaveAsViewModal: false });
    }

    _serializePeriodState(periodState) {
        if (!periodState || !periodState.selectedPeriod) return null;
        if (periodState.selectedPeriod === 'CUSTOM' && periodState.range) {
            return {
                type: 'FIXED',
                start_date: moment(periodState.range.from).format('YYYY-MM-DD'),
                end_date: moment(periodState.range.to).format('YYYY-MM-DD'),
            };
        }
        return {
            type: 'RELATIVE',
            predefined_period: periodState.selectedPeriod,
        };
    }

    _buildCurrentViewConfig() {
        // Snapshot del estado actual del explorador para persistir como DataExplorerView._config.
        // Se guarda en snake_case; el backend acepta tanto snake como camel y lo traduce
        // al payload del data-explorer/query al ejecutar la vista desde el portal.
        //
        // Fuente: `currentViewState` (estado VIVO del Header, incluye columnas y su orden),
        // NO `lastFilters` (snapshot de la última query). Así guardar y dirty quedan
        // consistentes y reaccionan al instante a cualquier cambio (incl. columnas).
        const { currentViewState, chartMetrics, chartTimePeriod, chartPrefs } = this.state;
        if (!currentViewState) return null;

        return {
            source_type: currentViewState.sourceType,
            selected_items: currentViewState.selectedItems || [],
            period: this._serializePeriodState(currentViewState.periodState),
            compare: !!currentViewState.compare,
            compare_period: currentViewState.compare
                ? this._serializePeriodState(currentViewState.comparePeriodState)
                : null,
            // Solo persistimos los filtros manuales del user. Los filtros auto-generados
            // por el Header (Cuenta ID, Plataforma) viven en `dimensions_filters_json` y se
            // regeneran solos al ejecutar la query con selected_items + source_type.
            dimensions_filters: currentViewState.dimensionsFilters || [],
            dimensions: currentViewState.dimensions || [],
            metrics: (currentViewState.metrics || []).map((m) => ({ name: m })),
            chart_metrics: (chartMetrics || [])
                .filter((m) => m !== null && m !== undefined)
                .map((m) => ({ name: m })),
            chart_time_period: chartTimePeriod || null,
            // Preferencias de visualización (KPI cards, bar chart, pie chart, tooltip).
            // Sincronizadas con LocalStorage via handleChartPrefsChange.
            chart_preferences: chartPrefs || {},
        };
    }

    _isAgencyUser() {
        return LocalStorage.fetchUserType() === 'AGENCY_USER';
    }

    _renderViewToolbar() {
        // Controles de Vista (selector + Guardar + estado + Compartir/descargar). Vive en el
        // Nivel 1 del header, al lado del Cliente (slot `viewToolbar`). Solo para agency users.
        if (!this._isAgencyUser()) return null;
        const agencyClientId = (this.state.currentViewState && this.state.currentViewState.agencyClientId)
            || (this.state.lastFilters && this.state.lastFilters.agencyClientId);
        return (
            <DataExplorerToolbar
                onDownloadPDF={this.handleDownloadPDF}
                onDownloadCSV={this.handleDownload}
                onExportDataNodeJson={this.handleExportDataNodeJson}
                onShareUrl={this.handleShareUrl}
                onSaveAsView={this.openSaveAsViewModal}
                onOpenSavedViews={this.openCommandPalette}
                currentView={this.state.currentView}
                agencyClientId={agencyClientId}
                onViewUpdated={this.handleViewUpdated}
                dirty={this._isViewDirty()}
                savingChanges={this.state.savingViewChanges}
                onSelectView={this.loadViewInline}
                onClearView={this.handleExitView}
                onSaveChanges={this.handleSaveViewChanges}
                onDiscardChanges={this.handleDiscardViewChanges}
                onDeleteView={this.handleDeleteView}
            />
        );
    }

    render() {
        if (this.state.hasAccess === false) {
            return <DataExplorerUpgradePage />;
        }

        if (this.state.hasAccess === null) {
            return (
                <div className="app-content">
                    <LayoutSidebar />
                    <section className="section-content">
                        <div className="loading-content">
                            <Spinner />
                        </div>
                    </section>
                </div>
            );
        }

        return (
            <div className="app-content">
                <LayoutSidebar />
                <section className="section-content data-explorer-page">
                    <header className="section-header">
                        <DataExplorerHeader
                            onExplore={this.handleExplore}
                            onDownload={this.handleDownload}
                            isDownloading={this.state.isDownloading}
                            onFieldsDataLoaded={this.handleFieldsDataLoaded}
                            onHeaderReady={this.handleHeaderReady}
                            onViewStateChange={this.handleViewStateChange}
                            viewToolbar={this._renderViewToolbar()}
                        />
                    </header>
                    <div className="section-body" style={{ position: 'relative', minHeight: '400px' }}>
                        <div style={{ position: 'relative', width: '100%' }}>
                            {this.state.loading && (
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 1000
                                }}>
                                    <Spinner />
                                </div>
                            )}
                            {/* Empty states: onboarding (sin clientes), error de backend,
                                cuenta caída, datos preparándose, sin resultados o error
                                genérico. Reemplaza el alert() bloqueante histórico. */}
                            {!this.state.loading && this._computeEmptyStateKind() ? (
                            <DataExplorerEmptyState
                                kind={this._computeEmptyStateKind()}
                                description={this.state.emptyStateMessage || undefined}
                                ctaContext={{
                                    agencyClientId:
                                        this.state.lastFilters &&
                                        this.state.lastFilters.agencyClientId,
                                }}
                                onRetry={
                                    this.state.emptyStateKind && this.state.lastFilters && this.state.emptyStateRetryable !== false
                                        ? () => this._executeExplore(this.state.lastFilters)
                                        : undefined
                                }
                            />
                        ) : (
                            <>
                                {/* 1. Métricas clave + gráficos de barras/torta */}
                                <div ref={this.chartsSectionRef}>
                                    <DataExplorerChartsSection
                                        key={`charts-${this.state.currentView ? this.state.currentView.id : 'free'}-${this.state.chartPrefsVersion}`}
                                        results={this.state.results}
                                        lastFilters={this.state.lastFilters}
                                        fieldsData={this.state.fieldsData}
                                        onChartPrefsChange={this.handleChartPrefsChange}
                                    />
                                </div>
                                {/* 2. Controles + gráfico temporal */}
                                {this.state.fieldsData && this.state.sourceType && (!this.state.loading || this.state.results) && !this.hasMetricFilters() && (
                                    <ChartControls
                                        metrics={this.state.chartMetrics}
                                        timePeriod={this.state.chartTimePeriod}
                                        availableTimePeriods={this.state.availableTimePeriods}
                                        defaultTimePeriod={this.state.defaultTimePeriod}
                                        fieldsData={this.state.fieldsData}
                                        sourceType={this.state.sourceType}
                                        onMetricsChange={this.handleChartMetricsChange}
                                        onTimePeriodChange={this.handleChartTimePeriodChange}
                                        showTooltip={this.state.showTooltip}
                                        onShowTooltipChange={this.handleShowTooltipChange}
                                        availableMetrics={this.state.results && this.state.results.chartData && this.state.results.chartData.series
                                            ? [...new Set(this.state.results.chartData.series.map(s => s.name))]
                                            : []}
                                    />
                                )}
                                {this.hasMetricFilters() ? (
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minHeight: '100px',
                                        padding: '1rem',
                                        textAlign: 'center',
                                        color: '#666'
                                    }}>
                                        <p>{this.props.t('data_explorer.remove_metric_filters_for_chart', 'Para ver el gráfico se deben remover los filtros sobre métricas')}</p>
                                    </div>
                                ) : (
                                    this.state.results && (
                                        <div ref={this.lineChartRef}>
                                            <DataExplorerLineChart
                                                chartData={this.state.results.chartData}
                                                selectedMetrics={this.state.chartMetrics}
                                                showTooltip={this.state.showTooltip}
                                            />
                                        </div>
                                    )
                                )}
                                {/* 3. Tabla */}
                                <div ref={this.tableRef}>
                                    <DataExplorerTable
                                        loading={false}
                                        results={this.state.results}
                                        fieldsData={this.state.fieldsData}
                                    />
                                </div>
                            </>
                        )}
                        </div>
                    </div>
                </section>
                <DataExplorerJsonModal
                    isOpen={this.state.isDataNodeJsonModalOpen}
                    toggle={() => this.setState({ isDataNodeJsonModalOpen: false })}
                    jsonContent={this.state.dataNodeRequestJson || ''}
                />
                <ConfigureEventsModal
                    isOpen={this.state.showEventsConfigModal}
                    unconfiguredAccounts={this.state.unconfiguredAccounts}
                    onComplete={() => {
                        this.setState({ showEventsConfigModal: false });
                        this._executeExplore(this.state.pendingExploreFilters);
                    }}
                    toggle={() => this.setState({ showEventsConfigModal: false, pendingExploreFilters: null })}
                />
                <SaveAsViewModal
                    isOpen={this.state.showSaveAsViewModal}
                    toggle={this.closeSaveAsViewModal}
                    agencyClientId={this.state.lastFilters && this.state.lastFilters.agencyClientId}
                    config={this._buildCurrentViewConfig()}
                    suggestedName={this.state.replicateSuggestedName}
                    onSaved={this.handleViewSaved}
                />
                <SavedViewsCommandPalette
                    isOpen={this.state.showCommandPalette}
                    toggle={this.closeCommandPalette}
                    agencyClientId={this.state.lastFilters && this.state.lastFilters.agencyClientId}
                    onBeforeNavigateAway={this.disableUnsavedGuard}
                />
                {this.state.confirmExitView && (
                    <YesNoModal
                        isOpen={this.state.confirmExitView}
                        title={this.props.t('saved_views.unsaved_warning_title')}
                        question={this.props.t('saved_views.unsaved_warning_body')}
                        onYesAnswer={() => {
                            // Si el confirm vino de cambiar a otra vista, cargamos esa vista
                            // (descartando los cambios). Si vino de salir, salimos.
                            const pending = this.state.pendingViewToLoad;
                            if (pending) {
                                this._applyViewInline(pending);
                            } else {
                                this._performExitView();
                            }
                        }}
                        onNoAnswer={() => this.setState({ confirmExitView: false, pendingViewToLoad: null })}
                    />
                )}
            </div>
        );
    }
}

export const DataExplorerPage = withTranslation()(DataExplorerPageComponent);
